import { useEffect, useState } from "react";
import CustomPagination from "../Pagination/CustomPagination";
import useStyles from "./style";
import { Box, Button, Flex, Table, Text } from "@mantine/core";
import { getAllPatients } from "../../services/patient";
import { useSelector } from "react-redux";
import TableLoader from "../TableLoader";
import { getAllStaff } from "../../services/staff";
import {
  getSkaiTokenPackages,
  getSkaiTokenUsage,
} from "../../services/skaiTokens";

const SubscriptionDetailsCard = () => {
  const { profile } = useSelector((state) => {
    return state.profile;
  });

  const { patients } = useSelector((state) => state.patient);
  const [clientCurrentPage, setClientCurrentPage] = useState(1);
  const [staffCurrentPage, setStaffCurrentPage] = useState(1);
  // Local loading states initialized to true — prevents stale Redux data from flashing on first render
  const [staffLoading, setStaffLoading] = useState(true);
  const [clientLoading, setClientLoading] = useState(true);
  const { classes } = useStyles();
  const { staffCount, staff } = useSelector((state) => state.staff);

  const planClientLimit = Number(profile?.subscription?.packageId?.patient || 0);
  const totalActiveClients = Number(patients?.count || 0);
  const overageClientCount = planClientLimit > 0
    ? Math.max(0, totalActiveClients - planClientLimit)
    : 0;
  const additionalClientCost =
    Number(profile?.user?.additionalClientCost) ||
    Number(profile?.subscription?.packageId?.additionalClient) ||
    0;

  const planStaffLimit = Number(profile?.subscription?.packageId?.staff || 0);
  const totalActiveStaff = Number(staffCount || 0);
  const overageStaffCount = planStaffLimit > 0
    ? Math.max(0, totalActiveStaff - planStaffLimit)
    : 0;
  const additionalStaffCost =
    Number(profile?.user?.additionalStaffCost) ||
    Number(profile?.subscription?.packageId?.additionalStaff) ||
    0;
  const tokenPackages = useSelector((state) => state.skai.tokenPackages);
  const usageData = useSelector((state) => state.skai.usageHistory);
  const usageArray = Array.isArray(usageData) ? usageData : [];
  const existingRecord = usageArray.find(
    (item) => item?.admin === profile?.user?.id,
  );
  const subscribedPackage = tokenPackages.find(
    (item) => item?._id === existingRecord?.aiPackageId,
  );

  useEffect(() => {
    setClientLoading(true);
    getAllPatients(clientCurrentPage, true, true).finally(() => setClientLoading(false));
  }, [clientCurrentPage]);

  useEffect(() => {
    setStaffLoading(true);
    getAllStaff(staffCurrentPage, true, true, "").finally(() => setStaffLoading(false));
  }, [staffCurrentPage]);

  useEffect(() => {
    getSkaiTokenPackages(true);
    getSkaiTokenUsage(profile?.user?.id);
  }, [profile?.user]);

  const handleClientPageChange = (pageNumber) => {
    setClientCurrentPage(pageNumber);
  };

  const handleStaffPageChange = (pageNumber) => {
    setStaffCurrentPage(pageNumber);
  };

  return (
    <Box className={classes.card}>
      <Flex className={classes.cardHdr}>
        <p className={classes.cardHdng}>Subscription Details</p>
      </Flex>
      <Box className={classes.tablesParent}>
        <Box className={classes.tableParent1}>
          <Table className={classes.table}>
            <thead>
              <tr>
                <th>Active Staffs</th>
              </tr>
            </thead>
            <tbody>
              {staffLoading ? (
                <tr>
                  <td>
                    <TableLoader />
                  </td>
                </tr>
              ) : staff?.staffs?.filter((s) => s?.type?.toLowerCase() !== "contractor")?.slice(0, 10)?.length > 0 ? (
                staff?.staffs?.filter((s) => s?.type?.toLowerCase() !== "contractor")?.slice(0, 10)?.map((staffData) => (
                  <tr>
                    <td>
                      {staffData?.name || ""}
                      {staffData?.role === "admin" ? " (Admin role)" : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td><Text align="center" color="grey" size="sm" >No Active Staff</Text></td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>
        <Box className={classes.tableParent2}>
          <Table className={classes.table}>
            <thead>
              <tr>
                <th>Active Clients</th>
              </tr>
            </thead>
            <tbody>
              {clientLoading ? (
                <tr>
                  <td>
                    <TableLoader />
                  </td>
                </tr>
              ) : patients?.patients?.slice(0, 10)?.length > 0 ? (
                patients?.patients?.slice(0, 10)?.map((patientData) => (
                  <tr>
                    <td>
                      {patientData?.name || ""}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td><Text align="center" color="grey" size="sm" ff={"Inter"} >No Active Clients</Text></td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>
        <Box className={classes.tableParent3}>
          <Table className={classes.table}>
            <thead>
              <tr>
                <th>Add-on Features</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Skai
                  {existingRecord && subscribedPackage && (
                    <span className={classes.tdAmount}>
                      + ${subscribedPackage?.price}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Evv</td>
              </tr>
              {overageClientCount > 0 && (
                <tr>
                  <td>
                    Additional Clients ({overageClientCount})
                    {additionalClientCost > 0 && (
                      <span className={classes.tdAmount}>
                        + ${overageClientCount * additionalClientCost}
                      </span>
                    )}
                  </td>
                </tr>
              )}
              {overageStaffCount > 0 && (
                <tr>
                  <td>
                    Additional Staff ({overageStaffCount})
                    {additionalStaffCost > 0 && (
                      <span className={classes.tdAmount}>
                        + ${overageStaffCount * additionalStaffCost}
                      </span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>

      </Box>
      <Flex className={classes.paginationRow}>
        <Box className="tableWidth">
          <CustomPagination
            data={staffCount}
            itemsPerPage={10}
            currentPage={staffCurrentPage}
            handlePageChange={handleStaffPageChange}
          />
        </Box>
        <Box className="tableWidth">
          <CustomPagination
            itemsPerPage={10}
            data={patients?.count}
            currentPage={clientCurrentPage}
            handlePageChange={handleClientPageChange}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default SubscriptionDetailsCard;
